<template>
  <avs-card>
    <template #title>
      <v-toolbar
        class="mt-4"
        elevation="0"
        color="transparent"
      >
        <div style="width: 100%">
          <div
            class="d-flex align-center mb-3"
            style="gap: 1rem; flex-wrap: wrap"
          >
            <h2 class="text-h5">
              <v-icon
                left
                color="primary"
                >mdi-chart-line</v-icon
              >
              {{ $t("Bilan Financier") }}
            </h2>
            <v-spacer />
            <v-btn
              color="primary"
              @click="exportToExcel"
              :loading="exporting"
              outlined
            >
              <v-icon left>mdi-download</v-icon>
              {{ $t("Exporter") }}
            </v-btn>
          </div>

          <!-- Filtres -->
          <v-card
            outlined
            class="pa-4"
          >
            <v-row align="center">
              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.start_date"
                      :label="$t('Date de début')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      outlined
                      hide-details
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.start_date"
                    @input="
                      startDateMenu = false
                      loadReport()
                    "
                    :max="filters.end_date"
                    locale="fr"
                  />
                </v-menu>
              </v-col>

              <v-col
                cols="12"
                sm="6"
                md="3"
              >
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="filters.end_date"
                      :label="$t('Date de fin')"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      dense
                      outlined
                      hide-details
                    />
                  </template>
                  <v-date-picker
                    v-model="filters.end_date"
                    @input="
                      endDateMenu = false
                      loadReport()
                    "
                    :min="filters.start_date"
                    locale="fr"
                  />
                </v-menu>
              </v-col>

              <v-col
                cols="12"
                sm="6"
                md="4"
              >
                <v-text-field
                  v-model="filters.customer"
                  :label="$t('Rechercher un client')"
                  prepend-icon="mdi-magnify"
                  @input="debouncedSearch"
                  clearable
                  dense
                  outlined
                  hide-details
                />
              </v-col>

              <v-col
                cols="12"
                sm="6"
                md="2"
              >
                <v-btn
                  color="primary"
                  block
                  @click="loadReport"
                  :loading="loading"
                >
                  <v-icon left>mdi-refresh</v-icon>
                  {{ $t("Actualiser") }}
                </v-btn>
              </v-col>
            </v-row>
          </v-card>
        </div>
      </v-toolbar>
    </template>

    <template #content>
      <v-container
        fluid
        class="pa-6"
      >
        <!-- Période affichée -->
        <v-alert
          type="info"
          color="secondary"
          outlined
          dense
          class="mb-4"
        >
          Période du {{ formatDate(filters.start_date) }} au {{ formatDate(filters.end_date) }}
        </v-alert>

        <!-- Cartes de résumé -->
        <v-row class="mb-6">
          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              class="pa-4"
              elevation="3"
              color="success"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Total Versements") }}</div>
                  <div class="text-h5 font-weight-bold">
                    {{ formatPrice(summary.total_payments) }}
                  </div>
                  <div class="text-caption">FCFA</div>
                </div>
                <v-icon
                  size="48"
                  color="white"
                  style="opacity: 0.5"
                  >mdi-cash-plus</v-icon
                >
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              class="pa-4"
              elevation="3"
              color="error"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Dépenses Approuvées") }}</div>
                  <div class="text-h5 font-weight-bold">
                    {{ formatPrice(summary.total_expenses_approved) }}
                  </div>
                  <div class="text-caption">FCFA</div>
                </div>
                <v-icon
                  size="48"
                  color="white"
                  style="opacity: 0.5"
                  >mdi-cash-minus</v-icon
                >
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              class="pa-4"
              elevation="3"
              color="warning"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Dépenses en Attente") }}</div>
                  <div class="text-h5 font-weight-bold">
                    {{ formatPrice(summary.total_expenses_pending) }}
                  </div>
                  <div class="text-caption">FCFA</div>
                </div>
                <v-icon
                  size="48"
                  color="white"
                  style="opacity: 0.5"
                  >mdi-clock-alert</v-icon
                >
              </div>
            </v-card>
          </v-col>

          <v-col
            cols="12"
            sm="6"
            md="3"
          >
            <v-card
              class="pa-4"
              elevation="3"
              :color="summary.balance >= 0 ? 'primary' : 'error'"
              dark
            >
              <div class="d-flex align-center justify-space-between">
                <div>
                  <div class="text-overline">{{ $t("Solde") }}</div>
                  <div class="text-h5 font-weight-bold">{{ formatPrice(summary.balance) }}</div>
                  <div class="text-caption">FCFA</div>
                </div>
                <v-icon
                  size="48"
                  color="white"
                  style="opacity: 0.5"
                >
                  {{ summary.balance >= 0 ? "mdi-trending-up" : "mdi-trending-down" }}
                </v-icon>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Résumé par événement -->
        <v-card
          class="mb-6"
          elevation="2"
        >
          <v-card-title class="primary white--text">
            <v-icon
              left
              color="white"
              >mdi-calendar-multiple</v-icon
            >
            {{ $t("Résumé par Événement") }}
          </v-card-title>
          <v-card-text class="pa-0">
            <v-simple-table>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">{{ $t("Événement") }}</th>
                    <th class="text-right">{{ $t("Versements") }}</th>
                    <th class="text-right">{{ $t("Dépenses") }}</th>
                    <th class="text-right">{{ $t("Solde") }}</th>
                    <th class="text-center">{{ $t("Statut") }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="eventsSummary.length === 0">
                    <td
                      colspan="5"
                      class="text-center grey--text py-8"
                    >
                      {{ $t("Aucun événement pour cette période") }}
                    </td>
                  </tr>
                  <tr
                    v-for="event in eventsSummary"
                    :key="event.event_id"
                  >
                    <td class="font-weight-medium">{{ event.event_name }}</td>
                    <td class="text-right">
                      <span class="green--text font-weight-bold">
                        +{{ formatPrice(event.total_payments) }} FCFA
                      </span>
                    </td>
                    <td class="text-right">
                      <span class="red--text font-weight-bold">
                        -{{ formatPrice(event.total_expenses) }} FCFA
                      </span>
                    </td>
                    <td
                      class="text-right font-weight-bold text-h6"
                      :class="event.balance >= 0 ? 'primary--text' : 'error--text'"
                    >
                      {{ formatPrice(event.balance) }} FCFA
                    </td>
                    <td class="text-center">
                      <v-chip
                        small
                        :color="event.balance >= 0 ? 'success' : 'error'"
                        text-color="white"
                      >
                        <v-icon
                          small
                          left
                        >
                          {{ event.balance >= 0 ? "mdi-check-circle" : "mdi-alert-circle" }}
                        </v-icon>
                        {{ event.balance >= 0 ? "Positif" : "Négatif" }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <!-- Tabs pour Versements et Dépenses -->
        <v-card elevation="2">
          <v-tabs
            v-model="activeTab"
            background-color="primary"
            dark
            grow
          >
            <v-tab>
              <v-icon left>mdi-cash-plus</v-icon>
              {{ $t("Versements") }}
              <v-chip
                small
                class="ml-2"
                color="white"
                text-color="primary"
              >
                {{ payments.length }}
              </v-chip>
            </v-tab>
            <v-tab>
              <v-icon left>mdi-cash-minus</v-icon>
              {{ $t("Dépenses") }}
              <v-chip
                small
                class="ml-2"
                color="white"
                text-color="primary"
              >
                {{ expenses.length }}
              </v-chip>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="activeTab">
            <!-- Tab Versements -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-text-field
                    v-model="searchTable"
                    append-icon="mdi-magnify"
                    :label="$t('Rechercher dans le tableau')"
                    single-line
                    hide-details
                    clearable
                    outlined
                    dense
                    class="mb-4"
                  />

                  <v-data-table
                    :headers="paymentsHeaders"
                    :items="payments"
                    :loading="loading"
                    :search="searchTable"
                    dense
                    :items-per-page="15"
                    :footer-props="{
                      'items-per-page-options': [10, 15, 25, 50, 100]
                    }"
                  >
                    <template #item.date="{ item }">
                      <div class="text-no-wrap">
                        {{ toDate(item.date) }}
                      </div>
                    </template>

                    <template #item.customer="{ item }">
                      <div>
                        <div class="font-weight-bold">
                          {{ item.customer.first_name }} {{ item.customer.last_name }}
                        </div>
                        <div class="text-caption grey--text">
                          <v-icon x-small>mdi-email</v-icon>
                          {{ item.customer.email }}
                        </div>
                        <div class="text-caption grey--text">
                          <v-icon x-small>mdi-phone</v-icon>
                          {{ item.customer.phone_number }}
                        </div>
                      </div>
                    </template>

                    <template #item.reservation="{ item }">
                      <div>
                        <div class="font-weight-medium">{{ item.reservation.name }}</div>
                        <v-chip
                          x-small
                          :color="getStatusColor(item.reservation.status)"
                          dark
                        >
                          {{ translateReservationStatus(item.reservation.status) }}
                        </v-chip>
                      </div>
                    </template>

                    <template #item.event="{ item }">
                      <v-chip
                        small
                        outlined
                        color="primary"
                      >
                        {{ item.event.name }}
                      </v-chip>
                    </template>

                    <template #item.amount="{ item }">
                      <span class="green--text font-weight-bold text-h6">
                        {{ formatPrice(item.amount) }} FCFA
                      </span>
                    </template>

                    <template #item.reservation_dates="{ item }">
                      <div class="text-caption">
                        <div>
                          <v-icon x-small>mdi-calendar-start</v-icon>
                          {{ toDate(item.reservation.start_date) }}
                        </div>
                        <div>
                          <v-icon x-small>mdi-calendar-end</v-icon>
                          {{ toDate(item.reservation.end_date) }}
                        </div>
                      </div>
                    </template>

                    <template #item.receipt_number="{ item }">
                      <v-chip
                        small
                        color="primary"
                        dark
                      >
                        <v-icon
                          small
                          left
                          >mdi-receipt</v-icon
                        >
                        {{ item.receipt_number }}
                      </v-chip>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Tab Dépenses -->
            <v-tab-item>
              <v-card flat>
                <v-card-text>
                  <v-text-field
                    v-model="searchTable"
                    append-icon="mdi-magnify"
                    :label="$t('Rechercher dans le tableau')"
                    single-line
                    hide-details
                    clearable
                    outlined
                    dense
                    class="mb-4"
                  />

                  <v-data-table
                    :headers="expensesHeaders"
                    :items="expenses"
                    :loading="loading"
                    :search="searchTable"
                    dense
                    :items-per-page="15"
                    :footer-props="{
                      'items-per-page-options': [10, 15, 25, 50, 100]
                    }"
                  >
                    <template #item.date="{ item }">
                      <div class="text-no-wrap">
                        {{ toDate(item.expense_date || item.date) }}
                      </div>
                    </template>

                    <template #item.event="{ item }">
                      <v-chip
                        small
                        outlined
                        color="primary"
                      >
                        {{ item.event.name }}
                      </v-chip>
                    </template>

                    <template #item.amount="{ item }">
                      <span class="red--text font-weight-bold text-h6">
                        {{ formatPrice(item.amount) }} FCFA
                      </span>
                    </template>

                    <template #item.status="{ item }">
                      <v-chip
                        small
                        :color="getExpenseStatusColor(item.status)"
                        dark
                      >
                        <v-icon
                          small
                          left
                        >
                          {{
                            item.status === "approved"
                              ? "mdi-check-circle"
                              : item.status === "pending"
                              ? "mdi-clock-alert"
                              : "mdi-close-circle"
                          }}
                        </v-icon>
                        {{ translateExpenseStatus(item.status) }}
                      </v-chip>
                    </template>

                    <template #item.expense_type="{ item }">
                      <v-chip
                        x-small
                        outlined
                      >
                        {{ item.expense_type }}
                      </v-chip>
                    </template>

                    <template #item.justification="{ item }">
                      <div
                        class="text-truncate"
                        style="max-width: 250px"
                      >
                        {{ item.justification || "-" }}
                      </div>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-container>
    </template>
  </avs-card>
</template>

<script>
import { FinancialService } from "@/services"
import { toDatetime } from "@/utils/datetime"
import _ from "lodash"

export default {
  name: "FinancialReport",
  data() {
    return {
      loading: false,
      exporting: false,
      activeTab: 0,
      startDateMenu: false,
      endDateMenu: false,
      searchTable: "",
      filters: {
        start_date: this.getFirstDayOfMonth(),
        end_date: this.getLastDayOfMonth(),
        customer: ""
      },
      payments: [],
      expenses: [],
      summary: {
        total_payments: 0,
        total_expenses_approved: 0,
        total_expenses_pending: 0,
        balance: 0
      },
      eventsSummary: [],
      paymentsHeaders: [
        { text: this.$t("Date"), value: "date", width: 170 },
        { text: this.$t("Client"), value: "customer", width: 220 },
        { text: this.$t("Réservation"), value: "reservation", width: 180 },
        { text: this.$t("Événement"), value: "event", width: 150 },
        { text: this.$t("Dates Réservation"), value: "reservation_dates", width: 170 },
        { text: this.$t("Reçu N°"), value: "receipt_number", width: 140 },
        { text: this.$t("Montant"), value: "amount", align: "right", width: 150 }
      ],
      expensesHeaders: [
        { text: this.$t("Date"), value: "date", width: 170 },
        { text: this.$t("Nom"), value: "name", width: 200 },
        { text: this.$t("Événement"), value: "event", width: 150 },
        { text: this.$t("Type"), value: "expense_type", width: 120 },
        { text: this.$t("Statut"), value: "status", width: 140 },
        { text: this.$t("Justification"), value: "justification", width: 250 },
        { text: this.$t("Montant"), value: "amount", align: "right", width: 150 }
      ]
    }
  },
  computed: {
    project() {
      return this.$store.state.app.project
    }
  },
  methods: {
    toDatetime,
    toDate(date) {
      if (!date) return "-"
      return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric"
      })
    },
    formatDate(dateStr) {
      if (!dateStr) return ""
      return new Date(dateStr).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric"
      })
    },
    formatPrice(v) {
      return v == null
        ? "0"
        : new Intl.NumberFormat("fr-FR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          }).format(v)
    },
    getFirstDayOfMonth() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split("T")[0]
    },
    getLastDayOfMonth() {
      const now = new Date()
      return new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split("T")[0]
    },
    getStatusColor(status) {
      const colors = {
        Closed: "grey",
        Ongoing: "green",
        Pending: "orange",
        Overdue: "red"
      }
      return colors[status] || "grey"
    },
    getExpenseStatusColor(status) {
      const colors = {
        pending: "orange",
        approved: "green",
        rejected: "red"
      }
      return colors[status] || "grey"
    },
    translateExpenseStatus(status) {
      const map = {
        pending: this.$t("En attente"),
        approved: this.$t("Approuvée"),
        rejected: this.$t("Rejetée")
      }
      return map[status] || status
    },
    translateReservationStatus(status) {
      const map = {
        Ongoing: this.$t("commons.status.ongoing"),
        Pending: this.$t("commons.status.pending"),
        Closed: this.$t("commons.status.closed"),
        Overdue: this.$t("commons.status.overdue"),
        Cancelled: this.$t("commons.status.cancelled")
      }
      return map[status] || status
    },
    async loadReport() {
      this.loading = true
      try {
        const params = {
          start_date: new Date(this.filters.start_date + "T00:00:00").toISOString(),
          end_date: new Date(this.filters.end_date + "T23:59:59").toISOString(),
          customer: this.filters.customer || ""
        }

        const data = await FinancialService.getFinancialReport(this, this.project.id, params)

        this.payments = data.payments || []
        this.expenses = data.expenses || []
        this.summary = data.summary || {
          total_payments: 0,
          total_expenses_approved: 0,
          total_expenses_pending: 0,
          balance: 0
        }
        this.eventsSummary = data.events_summary || []
      } catch (error) {
        console.error("Erreur:", error)
      } finally {
        this.loading = false
      }
    },
    debouncedSearch: _.debounce(function () {
      this.loadReport()
    }, 500),
    async exportToExcel() {
      this.exporting = true
      try {
        this.$store.commit("app/message", {
          msg: this.$t("Fonction d'export en cours de développement"),
          color: "info"
        })
      } finally {
        this.exporting = false
      }
    }
  },
  mounted() {
    this.loadReport()
  },
  activated() {
    this.loadReport()
  },
  beforeRouteEnter(to, from, next) {
    next(vm => vm.loadReport())
  }
}
</script>

<style scoped>
.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.text-no-wrap {
  white-space: nowrap;
}
</style>
